// The stylesheets in cascade order, later ones winning ties: fonts first,
// as an @import counts only at a stylesheet's top; then antd and the
// admin's own over it.
import './styles/fonts.css'
import './styles/variables.css'
import './styles/antd.css'
import './styles/button.css'
import './styles/breadscrumb.css'
import './styles/form.css'
import './styles/table.css'
import './styles/anonymous.css'
import './styles/header.css'
import './styles/layout.css'
import './styles/card.css'
import './styles/maps.css'
import './styles/components/devices/deviceTask.css'
import './styles/components/devices/mapyCz.css'
import './styles/components/devices/openingHours.css'
import './styles/components/location.css'
import './styles/animatedNumber.css'
import './styles/lang.css'
import './styles/group.css'
import './styles/timeChart.css'
import './styles/globals.css'
import './styles/design.css'
import 'dayjs/locale/cs'
import 'dayjs/locale/en'
import 'dayjs/locale/de'
import React, { Suspense, useEffect, useMemo } from 'react'
import { createRoot } from 'react-dom/client'
import { SWRConfig } from 'swr'
import { ConfigProvider } from 'antd'
import csCZ from 'antd/es/locale/cs_CZ'
import enGB from 'antd/es/locale/en_GB'
import deDE from 'antd/es/locale/de_DE'
import dayjs from 'dayjs'
import { defaultFetcher } from './utils/fetchers'
import { personsLang } from './utils/lang'
import { I18nProvider, loadDictionary } from './lib/i18n'
import useTranslation from './lib/i18n'
import { setPatterns, useRouter } from './lib/router'
import { routes } from './routes'
import Page404 from './pages/404'
import { LiveEventsProvider } from './components/liveEventsProvider'
import { SessionBridge } from './components/sessionBridge'
import { TourProvider } from './components/tour'
import { GtmNoScript } from './components/gtmNoScript'
import { ApplicationLoading } from './components/applicationLoading'
import { DemoRefusal } from './components/demoRefusal'

setPatterns(routes.map(([pattern]) => pattern))

const antdLocales = { cs: csCZ, en: enGB, de: deDE }
const swrConfig = { fetcher: defaultFetcher }

function App() {
  const { lang } = useTranslation()
  useEffect(() => {
    dayjs.locale(lang)
  }, [lang])
  return (
    <>
      <SessionBridge />
      <DemoRefusal />
      <SWRConfig value={swrConfig}>
        <ConfigProvider locale={antdLocales[lang]}>
          <LiveEventsProvider>
            <TourProvider>
              <Page />
            </TourProvider>
          </LiveEventsProvider>
        </ConfigProvider>
      </SWRConfig>
      <GtmNoScript />
    </>
  )
}

// The page the address names, mounted afresh when the page changes and
// kept while only its query does.
function Page() {
  const { pathname } = useRouter()
  const Shown = useMemo(
    () => routes.find(([pattern]) => pattern === pathname)?.[1] ?? Page404,
    [pathname]
  )
  return (
    <Suspense fallback={<ApplicationLoading />}>
      <Shown key={pathname} />
    </Suspense>
  )
}

const lang = personsLang()
loadDictionary(lang).then(() =>
  createRoot(document.getElementById('root') as HTMLElement).render(
    <I18nProvider initialLang={lang}>
      <App />
    </I18nProvider>
  )
)
